/**
 * SALASH — behaviour for the custom homepage sections.
 * Vanilla, dependency-free, safe to run more than once (Shopify section
 * re-renders in the theme editor).
 */

/* -------------------------------------------------------------------------
 * <sl-carousel> — scroll-snap track with prev/next controls
 * ---------------------------------------------------------------------- */
class SalashCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-sl-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-sl-prev]');
    this.next = this.querySelector('[data-sl-next]');

    this.onPrev = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onScroll = () => this.syncControls();

    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.observer = new ResizeObserver(() => this.syncControls());
    this.observer.observe(this.track);

    this.syncControls();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
  }

  get step() {
    const first = this.track.firstElementChild;
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  scrollByPage(direction) {
    // Advance by whole slides, but never more than a viewport at a time.
    const perView = Math.max(1, Math.floor(this.track.clientWidth / this.step));
    this.track.scrollBy({ left: this.step * perView * direction, behavior: 'smooth' });
  }

  syncControls() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;
    const overflowing = max > 2;

    this.toggleAttribute('data-static', !overflowing);
    if (this.prev) this.prev.disabled = !overflowing || scrollLeft <= 2;
    if (this.next) this.next.disabled = !overflowing || scrollLeft >= max - 2;
  }
}

/* -------------------------------------------------------------------------
 * <sl-tabs> — tab list switching sibling panels
 * ---------------------------------------------------------------------- */
class SalashTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-sl-tab]')];
    this.panels = [...this.querySelectorAll('[data-sl-panel]')];
    if (!this.tabs.length) return;

    this.onClick = (event) => {
      const tab = event.target.closest('[data-sl-tab]');
      if (!tab) return;
      this.select(tab.dataset.slTab);
    };

    this.onKeydown = (event) => {
      const keys = { ArrowRight: 1, ArrowLeft: -1 };
      const delta = keys[event.key];
      if (!delta) return;
      const index = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      const nextTab = this.tabs[(index + delta + this.tabs.length) % this.tabs.length];
      event.preventDefault();
      this.select(nextTab.dataset.slTab);
      nextTab.focus();
    };

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  select(id) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.slTab === id;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.slPanel !== id;
    });
  }
}

/* -------------------------------------------------------------------------
 * <sl-collapse> — wraps a <details> that only folds up on small screens.
 *
 * The markup ships with `open`, so above the breakpoint — or with JS off —
 * the content is just a plain row of links and the summary is hidden by CSS.
 *
 * Attributes:
 *   data-collapse-below  viewport width (px) under which it becomes a dropdown
 * ---------------------------------------------------------------------- */
class SalashCollapse extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;

    const below = parseInt(this.dataset.collapseBelow || '750', 10);
    this.query = window.matchMedia(`(max-width: ${below - 1}px)`);
    this.sync = () => {
      this.details.open = !this.query.matches;
    };

    this.query.addEventListener('change', this.sync);
    this.sync();
  }

  disconnectedCallback() {
    this.query?.removeEventListener('change', this.sync);
  }
}

/* -------------------------------------------------------------------------
 * <sl-hero> — slide rotation + lazy video playback
 * ---------------------------------------------------------------------- */
class SalashHero extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-sl-slide]')];
    this.dots = [...this.querySelectorAll('[data-sl-dot]')];
    this.index = 0;
    this.interval = parseInt(this.dataset.interval || '0', 10);

    this.onDot = (event) => {
      const dot = event.target.closest('[data-sl-dot]');
      if (!dot) return;
      this.stop();
      this.show(Number(dot.dataset.slDot));
    };
    this.addEventListener('click', this.onDot);

    // Only play hero video while it is on screen — saves battery and data.
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const videos = entry.target.querySelectorAll('video[data-sl-autoplay]');
        videos.forEach((video) => {
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        });
      });
    });
    this.io.observe(this);

    if (this.slides.length > 1 && this.interval > 0) this.start();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onDot);
    this.io?.disconnect();
    this.stop();
  }

  show(index) {
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}

/* -------------------------------------------------------------------------
 * <sl-typewriter> — types its own text out line by line behind a blinking
 * caret, then deletes and loops. Text comes from the element's own content,
 * split on newlines, so it still reads correctly with JS off.
 *
 * Attributes:
 *   data-speed  characters per second (default 20)
 *   data-loop   "true" to delete and retype forever (default), else type once
 * ---------------------------------------------------------------------- */
class SalashTypewriter extends HTMLElement {
  connectedCallback() {
    const raw = this.textContent.replace(/\r/g, '');
    this.lines = raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (!this.lines.length) return;

    this.total = this.lines.reduce((sum, line) => sum + line.length, 0);
    this.speed = Math.max(1, Number(this.dataset.speed) || 20);
    this.loop = this.dataset.loop !== 'false';

    this.build();

    // A ghost copy of the finished text holds the full height, so the section
    // below never jumps as characters are added and removed.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.paint(this.total, false);
      return;
    }

    this.typed = 0;
    this.direction = 1;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.start();
          else this.stop();
        });
      },
      { threshold: 0.25 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.stop();
  }

  build() {
    this.textContent = '';

    this.ghost = document.createElement('span');
    this.ghost.className = 'sl-type__ghost';
    this.ghost.textContent = this.lines.join('\n');

    this.live = document.createElement('span');
    this.live.className = 'sl-type__live';

    this.lineNodes = this.lines.map(() => {
      const node = document.createElement('span');
      node.className = 'sl-type__line';
      this.live.appendChild(node);
      return node;
    });

    this.caret = document.createElement('span');
    this.caret.className = 'sl-type__caret';
    this.caret.textContent = '_';

    this.append(this.ghost, this.live);
  }

  /* Distribute `count` characters across the lines, and park the caret on
     whichever line is currently being written. */
  paint(count, showCaret = true) {
    let remaining = count;
    let caretLine = 0;

    this.lines.forEach((line, index) => {
      const shown = Math.max(0, Math.min(remaining, line.length));
      this.lineNodes[index].textContent = line.slice(0, shown);
      if (shown > 0) caretLine = index;
      remaining -= line.length;
    });

    if (showCaret) this.lineNodes[caretLine].appendChild(this.caret);
    else this.caret.remove();
  }

  start() {
    if (this.timer) return;
    const tick = () => {
      this.typed += this.direction;
      this.paint(this.typed);

      let delay = 1000 / this.speed;
      if (this.typed >= this.total) {
        if (!this.loop) {
          this.timer = null;
          return;
        }
        this.direction = -1;
        delay = 1400;
      } else if (this.typed <= 0) {
        this.direction = 1;
        delay = 500;
      } else if (this.direction === -1) {
        delay = 500 / this.speed;
      }

      this.timer = setTimeout(tick, delay);
    };
    this.timer = setTimeout(tick, 400);
  }

  stop() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}

/* -------------------------------------------------------------------------
 * <sl-booking> — free event RSVP, posted to a Google Apps Script endpoint
 *
 * No cart, no checkout, no Shopify order. Requests land as rows in a sheet.
 * Capacity is set per event in the theme editor and enforced server-side inside
 * a lock; the seat count fetched on load is only there to close a full event
 * before anyone bothers filling the form in.
 * ---------------------------------------------------------------------- */

// One request per endpoint per page, shared by every event on it.
const bookingCounts = new Map();

function fetchBookingCounts(endpoint) {
  if (!bookingCounts.has(endpoint)) {
    bookingCounts.set(
      endpoint,
      fetch(endpoint)
        .then((res) => res.json())
        .then((data) => (data && data.ok ? data.booked || {} : {}))
        // A failed count must never block booking — the server checks again anyway.
        .catch(() => ({}))
    );
  }

  return bookingCounts.get(endpoint);
}

class SalashBooking extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.status = this.querySelector('[data-sl-status]');
    this.submit = this.querySelector('[type="submit"]');
    this.guests = this.querySelector('[data-sl-guests]');
    this.source = this.querySelector('[data-sl-source]');
    this.specify = this.querySelector('[data-sl-specify]');
    this.specifyInput = this.specify ? this.specify.querySelector('input') : null;

    this.endpoint = this.dataset.endpoint;
    this.capacity = parseInt(this.dataset.capacity, 10) || 0;

    this.onSource = () => this.syncSpecify();
    this.onSubmit = (event) => this.send(event);

    // Apps Script cold-starts, which is most of the wait on the first submit of
    // the day. Touching the endpoint the moment someone starts filling the form
    // warms the instance while they type, so the submit itself lands warm.
    // fetchBookingCounts is memoised per endpoint, so this costs nothing when
    // syncSeats has already run.
    this.onFocus = () => {
      if (this.endpoint) fetchBookingCounts(this.endpoint);
    };

    this.source?.addEventListener('change', this.onSource);
    this.form.addEventListener('submit', this.onSubmit);
    this.form.addEventListener('focusin', this.onFocus, { once: true });

    this.syncSpecify();
    this.syncSeats();
  }

  disconnectedCallback() {
    this.source?.removeEventListener('change', this.onSource);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.form?.removeEventListener('focusin', this.onFocus);
  }

  /* Reveal "Please specify" only for answers flagged with data-specify. Disabled
     while hidden, so its `required` can't block a submit the guest can't see. */
  syncSpecify() {
    if (!this.specify || !this.specifyInput) return;

    const option = this.source?.selectedOptions[0];
    const wanted = option?.dataset.specify === 'true';

    this.specify.hidden = !wanted;
    this.specifyInput.disabled = !wanted;
    this.specifyInput.required = wanted;
    if (!wanted) this.specifyInput.value = '';
  }

  async syncSeats() {
    if (!this.endpoint || !this.capacity) return;

    const counts = await fetchBookingCounts(this.endpoint);
    const booked = parseInt(counts[this.dataset.eventId], 10) || 0;
    const left = this.capacity - booked;

    if (left <= 0) {
      this.close(this.dataset.fullLabel || 'Fully booked');
      return;
    }

    // Don't offer more places in the dropdown than actually remain. Disabled as
    // well as hidden — Safari has never reliably honoured hidden on an option.
    if (this.guests) {
      Array.from(this.guests.options).forEach((option) => {
        const tooMany = parseInt(option.value, 10) > left;
        option.hidden = tooMany;
        option.disabled = tooMany;
      });
    }
  }

  /* The dialling code lives in its own select, so rejoin the two before the
     row is written. A number already typed in international form (+20…, 0020…)
     keeps what the guest wrote rather than being prefixed twice. */
  fullPhone(data) {
    const code = String(data.get('phoneCode') || '').trim();
    const number = String(data.get('phone') || '').trim();

    if (!number) return '';
    if (!code || number.startsWith('+') || number.startsWith('00')) return number;

    // A leading 0 is the national trunk prefix and is dropped when dialling in.
    return code + ' ' + number.replace(/^0+/, '');
  }

  close(message) {
    this.form.hidden = true;
    this.say(message, 'error');
  }

  say(message, kind) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = false;
    this.status.className = 'sl-book__status sl-book__status--' + (kind || 'done');
  }

  async send(event) {
    event.preventDefault();
    if (this.sending) return;

    const data = new FormData(this.form);
    const source = String(data.get('source') || '');
    const detail = String(data.get('sourceDetail') || '').trim();

    const payload = {
      secret: this.dataset.secret,
      eventId: this.dataset.eventId,
      event: this.dataset.event,
      eventDate: this.dataset.eventDate,
      capacity: this.capacity,
      firstName: String(data.get('firstName') || '').trim(),
      lastName: String(data.get('lastName') || '').trim(),
      email: String(data.get('email') || '').trim(),
      phone: this.fullPhone(data),
      guests: parseInt(data.get('guests'), 10) || 1,
      heardVia: detail ? source + ' — ' + detail : source,
      company: String(data.get('company') || ''),
      details: {},
    };

    this.sending = true;
    this.label = this.submit.textContent;
    this.submit.disabled = true;
    this.submit.textContent = 'Sending…';

    try {
      // No custom headers and no Content-Type, deliberately: that keeps this a
      // CORS "simple request" and avoids a preflight, which Apps Script does
      // not answer. Adding Content-Type: application/json breaks this.
      const res = await fetch(this.endpoint, { method: 'POST', body: JSON.stringify(payload) });
      const result = await res.json();

      if (result.ok) {
        this.form.hidden = true;
        this.say(this.dataset.success || 'Thank you — we have your request.');
        return;
      }

      this.fail(result);
    } catch (error) {
      this.say('Something went wrong sending your request. Please email us instead.', 'error');
    } finally {
      this.sending = false;
      this.submit.disabled = false;
      this.submit.textContent = this.label;
    }
  }

  fail(result) {
    const left = result.seatsLeft;

    switch (result.reason) {
      case 'full':
      case 'closed':
        this.close(this.dataset.fullLabel || 'Fully booked');
        break;
      case 'partial':
        this.say(
          left === 1 ? 'Only 1 place left — please adjust your guests.' : 'Only ' + left + ' places left — please adjust your guests.',
          'error'
        );
        break;
      case 'duplicate':
        this.form.hidden = true;
        this.say('You are already on the list for this event.');
        break;
      case 'email':
        this.say('That email address does not look right.', 'error');
        break;
      default:
        this.say('We could not save your request. Please email us instead.', 'error');
    }
  }
}

/* -------------------------------------------------------------------------
 * <sl-restock> — back in stock waitlist, posted to a Google Apps Script endpoint
 *
 * Its own endpoint, sheet and deployment, separate from <sl-booking>. Nobody is
 * emailed automatically, here or when stock returns; the sheet is the point.
 * See restock-waitlist-plan.md.
 * ---------------------------------------------------------------------- */

// One warm-up request per endpoint per page, whatever opens the form.
const restockWarmed = new Set();

function warmRestock(endpoint) {
  if (!endpoint || restockWarmed.has(endpoint)) return;
  restockWarmed.add(endpoint);
  // Apps Script cold-starts, which is most of the wait on the first submit of
  // the day. Touching the endpoint as the form opens warms the instance while
  // they type. The response is not used, and a failure here changes nothing.
  fetch(endpoint).catch(() => {});
}

class SalashRestock extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.toggle = this.querySelector('[data-sl-toggle]');
    this.panel = this.querySelector('[data-sl-panel]');
    if (!this.form || !this.toggle || !this.panel) return;

    this.status = this.querySelector('[data-sl-status]');
    this.submit = this.form.querySelector('[type="submit"]');
    this.email = this.form.querySelector('[name="email"]');

    this.endpoint = this.dataset.endpoint;

    this.onToggle = () => this.open(this.panel.hidden);
    this.onSubmit = (event) => this.send(event);

    this.toggle.addEventListener('click', this.onToggle);
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggle);
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  open(wanted) {
    this.panel.hidden = !wanted;
    this.toggle.setAttribute('aria-expanded', String(wanted));

    if (!wanted) return;
    warmRestock(this.endpoint);
    this.email?.focus();
  }

  say(message, kind) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = false;
    this.status.className = 'sl-restock__status sl-restock__status--' + (kind || 'done');
  }

  // Nothing reached the sheet, so give them a way through that does not depend
  // on us. Without a fallback address configured, just say what happened.
  fallback(message) {
    const address = this.dataset.contactEmail;
    this.say(address ? message + ' Please email ' + address + ' instead.' : message, 'error');
  }

  async send(event) {
    event.preventDefault();
    if (this.sending) return;

    const data = new FormData(this.form);

    const payload = {
      secret: this.dataset.secret,
      product: this.dataset.product,
      variant: this.dataset.variant,
      sku: this.dataset.sku,
      url: this.dataset.url,
      name: String(data.get('name') || '').trim(),
      email: String(data.get('email') || '').trim(),
      company: String(data.get('company') || ''),
    };

    this.sending = true;
    this.label = this.submit.textContent;
    this.submit.disabled = true;
    this.submit.textContent = 'Sending…';

    try {
      // No custom headers and no Content-Type, deliberately: that keeps this a
      // CORS "simple request" and avoids a preflight, which Apps Script does
      // not answer. Adding Content-Type: application/json breaks this.
      const res = await fetch(this.endpoint, { method: 'POST', body: JSON.stringify(payload) });
      const result = await res.json();

      if (result.ok) {
        this.form.hidden = true;
        this.say(this.dataset.success || 'Thank you — you are on the list.');
        return;
      }

      this.fail(result);
    } catch (error) {
      this.fallback('Something went wrong sending your request.');
    } finally {
      this.sending = false;
      this.submit.disabled = false;
      this.submit.textContent = this.label;
    }
  }

  fail(result) {
    switch (result.reason) {
      case 'duplicate':
        this.form.hidden = true;
        this.say('You are already on the list for this piece.');
        break;
      case 'email':
        this.say('That email address does not look right.', 'error');
        break;
      default:
        this.fallback('We could not save your request.');
    }
  }
}

if (!customElements.get('sl-carousel')) customElements.define('sl-carousel', SalashCarousel);
if (!customElements.get('sl-typewriter')) customElements.define('sl-typewriter', SalashTypewriter);
if (!customElements.get('sl-tabs')) customElements.define('sl-tabs', SalashTabs);
if (!customElements.get('sl-collapse')) customElements.define('sl-collapse', SalashCollapse);
if (!customElements.get('sl-hero')) customElements.define('sl-hero', SalashHero);
if (!customElements.get('sl-booking')) customElements.define('sl-booking', SalashBooking);
if (!customElements.get('sl-restock')) customElements.define('sl-restock', SalashRestock);

/* -------------------------------------------------------------------------
 * Reveal on scroll — opt in with class="sl-reveal"
 * ---------------------------------------------------------------------- */
(function reveal() {
  const revealAll = (root = document) => root.querySelectorAll('.sl-reveal').forEach((el) => el.classList.add('is-in'));

  // Anything that would keep the observer from running must still leave the
  // page readable, so fall through to showing everything.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
    revealAll();
    document.addEventListener('shopify:section:load', (event) => revealAll(event.target));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );

  const watch = (root = document) => root.querySelectorAll('.sl-reveal:not(.is-in)').forEach((el) => observer.observe(el));

  watch();
  document.addEventListener('shopify:section:load', (event) => watch(event.target));
})();

/* -------------------------------------------------------------------------
 * Header menu — keep "More" for genuine overflow only
 * <overflow-list> puts the "More" button into the row *before* it measures,
 * so the links are always budgeted one button's worth of space short. A menu
 * that fits exactly still loses its last link to a dropdown that then holds a
 * single item. While the full row fits we hold the component disabled; the
 * moment it doesn't we hand control straight back, so the drawer breakpoint
 * (driven by the component's own overflowMinimum event) still works.
 * ---------------------------------------------------------------------- */
(function headerMenuOverflow() {
  const SELECTOR = 'header-menu overflow-list';

  let list = null;
  let mutations = null;
  let scheduled = false;
  let measuredAt = -1;
  let fits = false;

  const part = (name) => list.shadowRoot?.querySelector(`[part="${name}"]`) ?? null;

  // The component only sets --overflow-count once it has run a pass, which is
  // also when its internal refs exist. Calling showAll() before then throws.
  const ready = () => typeof list?.showAll === 'function' && list.style.getPropertyValue('--overflow-count') !== '';

  const handBack = () => {
    // showAll() collapses the placeholder, and a reflow leaves the row wrapping.
    // Both have to go back for the component to measure itself correctly.
    const placeholder = part('placeholder');
    placeholder?.style.removeProperty('width');
    placeholder?.style.removeProperty('display');
    list.removeAttribute('disabled');
  };

  const tune = () => {
    if (!ready()) return;

    const row = part('list');

    // Behind the drawer the row is display:none — nothing to measure, and
    // disabling it here would strand it with its observers switched off.
    if (!row || row.clientWidth === 0) return;

    if (window.innerWidth === measuredAt) {
      // Already decided for this viewport width. Re-apply rather than
      // re-measure, so the component can't quietly re-collapse on its own pass.
      if (fits) list.showAll();
      return;
    }

    list.showAll();

    // A reflow leaves flex-wrap: wrap on the row, under which content never
    // overflows horizontally — it just breaks to a second line.
    row.style.setProperty('flex-wrap', 'nowrap');

    fits = row.scrollWidth <= row.clientWidth + 1;
    measuredAt = window.innerWidth;

    if (fits) {
      list.removeAttribute('minimum-reached');
    } else {
      handBack();
    }
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      tune();
    });
  };

  const attach = () => {
    mutations?.disconnect();
    list = document.querySelector(SELECTOR);
    measuredAt = -1;
    if (!list) return;

    // Every collapse moves items between slots, so this catches the component's
    // own passes — including the first one, if it beats us to the page.
    mutations = new MutationObserver(schedule);
    mutations.observe(list, { attributes: true, attributeFilter: ['slot'], subtree: true });
    schedule();
  };

  window.addEventListener(
    'resize',
    () => {
      measuredAt = -1;
      schedule();
    },
    { passive: true }
  );

  window.addEventListener('load', schedule);

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector?.(SELECTOR)) attach();
  });

  customElements.whenDefined('overflow-list').then(attach);
})();
